import React, { useState, useEffect } from "react";
import { ShippingInfo } from "../../Interfaces/ShippingInfo";
import { useNavigate } from "react-router-dom";
import { usePopup } from "../Popup/PopupContext";
import PopupModel from "../Popup/PopupModel";
import { useCart } from "../../contexts/Cart/CartContext";
import { stateAbbreviations } from "../../CommonFunctions";
import "./CheckoutPageComponents.css";

import config from "../../config";
import apiClient from "../../services/apiClient";
const BASE_API = config.API_BASE_URL;

export interface ShippingFormInfo {
  city: string;
  state: string;
  street_address: string;
  apt_suite?: string;
  zip_code: string;
  first_name: string;
  last_name: string;
}

// used for getting info back from calling the shippo api
interface RecievedAddress {
  city: string;
  state: string;
  street_address: string;
  apt_suite?: string;
  zip_code: string;
}

interface ShippingFormProps {
  setIsCheckoutEnabled: (value: boolean) => void;
  setShippingInfo: React.Dispatch<React.SetStateAction<ShippingInfo>>;
  needShippingInfo?: boolean; //optional shipping option to gray scale when not needed
  isFromCreateFundraiser?: boolean;
}

// used to delay updating a variable until "delay" ms pass
function useDebounce<T>(value: T, delay: number): T {
  // value = form info, delay = time delay in ms
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler); // Cleanup timeout if value changes before delay ends
    };
  }, [value, delay]);

  return debouncedValue;
}

const ShippingForm: React.FC<ShippingFormProps> = ({
  setIsCheckoutEnabled,
  setShippingInfo,
  needShippingInfo = false,
  isFromCreateFundraiser = false,
}) => {
  const [formData, setFormData] = useState<ShippingFormInfo>({
    first_name: "",
    last_name: "",
    street_address: "",
    apt_suite: "",
    city: "",
    state: "",
    zip_code: "",
  });

  const { state, subtotal, totalItems: lineItemCount } = useCart();
  const { cart } = state;

  const [isInteracting, setIsInteracting] = useState(false);
  const [unknownError, setUnknownError] = useState(false);

  const debouncedFormData = useDebounce(formData, 1000); // 1000 ms = 1 sec delay

  const DONATION_ID = config.DONATION_ITEM_ID;
  const BUYOUT_ID = config.BUYOUT_ITEM_ID;
  const donationCount = cart.reduce((sum, item) => {
    if (item.sku === DONATION_ID || item.sku === BUYOUT_ID) {
      return sum + item.quantity;
    }
    return sum;
  }, 0);
  const productCount = lineItemCount - donationCount;
  const hideShippingForm =
    (productCount === 0 && !isFromCreateFundraiser) || !needShippingInfo;

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setIsInteracting(true);
    setUnknownError(false);
    setIsCheckoutEnabled(false); // dont allow submissions while form is edited
    setGetTaxes(false);
    // reset shipping info while form is being updated
    setShippingInfo({
      ship_to_name: "",
      street_address: "",
      apt_suite: "",
      city: "",
      state: "",
      zip_code: "",
    });
  };

  const navigate = useNavigate();
  // Error state for validation messages
  const [errors, setErrors] = useState<string[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);

  const { showPopup, hidePopup } = usePopup();
  const [currentAddress, setCurrentAddress] = useState<RecievedAddress | null>(
    null
  );
  const [shippingComplete, setShippingComplete] = useState(false);
  const [getTaxes, setGetTaxes] = useState(false);
  const [useUserAddress, setUseUserAddress] = useState(false);

  const getFormattedInputAddress = () => {
    return (
      <div style={{ marginTop: "10px", marginBottom: "10px" }}>
        <div>{formData.street_address}</div>
        {formData.apt_suite && <div>{formData.apt_suite}</div>}
        <div>
          {formData.city}, {formData.state} {formData.zip_code}
        </div>
      </div>
    );
  };

  const handleShippingWarningPopup = () => {
    const warningsList = (
      <ul>
        {warnings.map((warning, index) => (
          <li key={index}>{warning}</li>
        ))}
      </ul>
    );
    let formattedAddress;
    if (!currentAddress) {
      //bug catching in case there is a null address for some reason
      formattedAddress = <div> N/A </div>;
    } else {
      formattedAddress = (
        <div style={{ marginTop: "10px", marginBottom: "10px" }}>
          <strong>The following modified address was found:</strong>
          <div>{currentAddress.street_address}</div>
          {currentAddress.apt_suite && <div>{currentAddress.apt_suite}</div>}
          <div>
            {currentAddress.city}, {currentAddress.state}{" "}
            {currentAddress.zip_code}
          </div>
        </div>
      );
    }
    const shippingWarningPopup = new PopupModel(
      "Shipping - Warning", // header
      (
        <div>
          <strong>The provided original address</strong>
          {getFormattedInputAddress()}
          <strong> may not be correct for the following reasons: </strong>
          {warningsList}
          {formattedAddress}
        </div> //body
      ),
      [
        {
          label: "Use Modified",
          onClick: () => {
            setGetTaxes(true);
            setUseUserAddress(false);
            hidePopup();
          },
        },
        {
          label: "Use Original",
          onClick: () => {
            setGetTaxes(true);
            setUseUserAddress(true);
            hidePopup();
          },
        },
      ],
      "40vw" //width
    );

    setIsCheckoutEnabled(false);
    setGetTaxes(false);
    showPopup(
      shippingWarningPopup.header,
      shippingWarningPopup.body,
      shippingWarningPopup.buttons,
      shippingWarningPopup.width
    );
    setWarnings([]);
  };

  const handleShippingErrorPopup = () => {
    const errorList = (
      <ul>
        {errors.map((err, index) => (
          <li key={index}>{err}</li>
        ))}
      </ul>
    );
    const shippingErrorPopup = new PopupModel(
      "Shipping - Error", // header
      (
        <div>
          <strong>Invalid address was inputted</strong>
          {getFormattedInputAddress()}
          <strong>due to the following:</strong>
          {errorList}
        </div> //body
      ),
      [
        //buttons
      ],
      "40vw" //width
    );

    showPopup(
      shippingErrorPopup.header,
      shippingErrorPopup.body,
      shippingErrorPopup.buttons,
      shippingErrorPopup.width
    );
    setShippingComplete(false);
  };

  const updateAddressAndWarnings = (
    shippoAddress: RecievedAddress,
    shippingWarnings: string[]
  ) => {
    setCurrentAddress(shippoAddress);
    setWarnings(shippingWarnings);

    // Manually trigger the popup after the state has updated
    handleShippingWarningPopup();
  };

  const calculateTaxInfo = async () => {
    let useAddress: ShippingInfo;
    if (useUserAddress === true) {
      // use the user inputted address located in formdata
      useAddress = {
        ship_to_name: formData.first_name + " " + formData.last_name,
        street_address: formData.street_address,
        apt_suite: formData.apt_suite,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
      };
      setShippingInfo(useAddress);
      if (isFromCreateFundraiser) {
        setIsCheckoutEnabled(true);
      }
    } else if (currentAddress !== null) {
      // use the information returned to stripe located in currentaddress
      // update the form data to display the changed information
      setFormData({
        ...formData,
        street_address: currentAddress.street_address,
        apt_suite: currentAddress.apt_suite,
        city: currentAddress.city,
        state: currentAddress.state,
        zip_code: currentAddress.zip_code,
      });

      useAddress = {
        ship_to_name: formData.first_name + " " + formData.last_name,
        street_address: currentAddress.street_address,
        apt_suite: currentAddress.apt_suite,
        city: currentAddress.city,
        state: currentAddress.state,
        zip_code: currentAddress.zip_code,
      };
      setShippingInfo(useAddress);
      if (isFromCreateFundraiser) {
        setIsCheckoutEnabled(true);
      }
    } else {
      // for some reason currentAddress is null (shouldnt happen)
      console.error("Error! Current address was null");
    }
  };

  // Function to validate shipping information
  const validateShippingInfo = async () => {
    const { street_address, city, state, zip_code, first_name, last_name } =
      formData;

    const isShippingComplete =
      street_address !== "" &&
      city !== "" &&
      state !== "" &&
      zip_code !== "" &&
      first_name !== "" &&
      last_name != ""; // ok if apt is empty

    const newErrors: string[] = [];
    const shippingWarnings: string[] = [];

    if (isShippingComplete) {
      // Perform validation if all fields are filled
      if (!/^\d{5}$/.test(zip_code)) {
        // makes sure we have a 5 digit zip before continuing so we don't get autofilled by shippo's api/waste calls
        setShippingComplete(false);
        return;
      }
      // call shippo api to check validity
      // only call shippo if we know zip and state are good length so we arent wasting calls
      try {
        const response = await apiClient.post(
          `${BASE_API}checkout/check-shipping-info/`,
          {
            formData,
          }
        );
        const messages = response.data["message"];
        const shippoAddress = response.data["address"];
        if (response.data["is_valid"] === false) {
          if (Array.isArray(messages) && messages.length > 0) {
            messages.forEach((message: string) => {
              newErrors.push(message); // Add each message to newErrors
            });
          }
        }
        // if there's no warnings, we are safe to continue
        // if there are warnings, create a popup
        else if (Array.isArray(messages) && messages.length > 0) {
          messages.forEach((message: string) => {
            shippingWarnings.push(message); // Add each message to newErrors
          });
          setCurrentAddress(shippoAddress);
          setWarnings(shippingWarnings);
        }
        if (newErrors.length === 0) {
          setUseUserAddress(true);
        }
      } catch {
        setUnknownError(true);
      }
    }

    // Update errors and validation status
    setErrors(newErrors);
    // only if we had no errors and no warnings (shipping 100% fine) immediately set shipping to true
    setShippingComplete(
      isShippingComplete &&
        shippingWarnings.length === 0 &&
        newErrors.length === 0
    );
  };

  useEffect(() => {
    if (isInteracting) {
      validateShippingInfo(); //handle after delay of not typing for x seconds
      setIsInteracting(false); // Reset interaction state
    }
  }, [debouncedFormData]);

  useEffect(() => {
    // Only show the popup if both `currentAddress` and `warnings` have valid data
    if (currentAddress && warnings.length > 0) {
      setIsCheckoutEnabled(false);
      setGetTaxes(false);
      handleShippingWarningPopup();
    }
  }, [currentAddress, warnings]);

  useEffect(() => {
    // Only show the popup if both `currentAddress` and `warnings` have valid data
    if (errors.length > 0) {
      handleShippingErrorPopup();
    } else if (shippingComplete === true) {
      setGetTaxes(true);
      setShippingComplete(false);
    }
  }, [errors, shippingComplete]);

  useEffect(() => {
    // Only show the popup if both `currentAddress` and `warnings` have valid data
    if (getTaxes === true) {
      //we passed the shipping check, call the api
      calculateTaxInfo();
    }
  }, [getTaxes]);

  const handleBlur = () => {
    validateShippingInfo(); //handle after user clicks off form
  };

  return (
    <div
      style={{
        position: "relative",
      }}
    >
      {hideShippingForm && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1,
          }}
        ></div>
      )}
      <form
        autoComplete="off"
        style={{
          ...styles.formContainer,
          filter: hideShippingForm ? "grayscale(100%)" : "none",
          pointerEvents: hideShippingForm ? "none" : "auto",
        }}
      >
        <div style={styles.shippingBox}>
          <div style={styles.shippingHeader}>Shipping</div>
          <div className="checkout-page-shipping-form-group row">
            <div className="checkout-page-shipping-form-col">
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                style={styles.inputField}
                placeholder="First Name"
                required
              />
            </div>
            <div className="checkout-page-shipping-form-col">
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                style={styles.inputField}
                placeholder="Last Name"
                required
              />
            </div>
          </div>
          <div className="checkout-page-shipping-form-group row">
            <div
              className="checkout-page-shipping-form-col"
              style={{ flex: "0 0 66%" }}
            >
              <input
                type="text"
                id="street_address"
                name="street_address"
                value={formData.street_address}
                onChange={handleChange}
                style={styles.inputField}
                placeholder="Address"
                required
              />
            </div>
            <div
              className="checkout-page-shipping-form-col"
              style={{ flex: "0 0 30%" }}
            >
              <input
                type="text"
                id="apt_suite"
                name="apt_suite"
                value={formData.apt_suite}
                onChange={handleChange}
                style={styles.inputField}
                placeholder="Apt # / Suite"
              />
            </div>
          </div>
          <div className="checkout-page-shipping-form-group row">
            <div className="checkout-page-shipping-form-col">
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                style={styles.inputField}
                placeholder="City"
                required
              />
            </div>
            <div className="checkout-page-shipping-form-col">
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                style={styles.inputField}
                required
              >
                <option value="" disabled>
                  Select State
                </option>
                {stateAbbreviations.map((state, index) => (
                  <option key={index} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
            <div className="checkout-page-shipping-form-col">
              <input
                type="text"
                id="zip_code"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                style={styles.inputField}
                placeholder="Zip Code"
                required
              />
            </div>
          </div>
          {/* Ocassioanly ill get an error 400 from the api response. Without any direct reason as to why. Simply reprompting the api by
        entering the zip code again seems to fix the issue.*/}
          {unknownError && (
            <div style={styles.shippingError}>
              An unknown error occured. Please reenter your zipcode
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default ShippingForm;

const styles = {
  formContainer: {
    padding: "1% 2.5%",
  },
  shippingHeader: {
    fontSize: "2vm",
    marginBottom: "1vm",
    fontWeight: "700",
  },
  shippingBox: {
    border: "2px solid #aaa",
    padding: "3%",
    marginTop: "1vh",
    marginBottom: "3%",
    borderRadius: "0",
    backgroundColor: "#ffffff",
  },
  inputField: {
    border: "2px solid #ccc",
    borderRadius: "0", // Sharp corners
    backgroundColor: "white", // White background
    outline: "none",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.05)",
  },
  shippingError: {
    color: "#FF0000",

    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};
